import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Feather, ArrowRight, ArrowLeft, Loader2, Sparkles, Info, Wand2,
} from "lucide-react";
import ObituariesTabs from "@/components/ObituariesTabs";
import CemeteryPicker from "@/components/CemeteryPicker";

const AI_LANGS = [
  { code: "en", name: "English" },
  { code: "ml", name: "Malayalam" },
  { code: "hi", name: "Hindi" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "kn", name: "Kannada" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "ar", name: "Arabic" },
  { code: "pt", name: "Portuguese" },
  { code: "de", name: "German" },
];

// -----------------------------------------------------------------------------
// /obituaries/submit — publish an Obituary / Death Announcement.
//
// Under the hood every obituary is stored as a memorial with
// `memorial_type = "death_announcement"`, so it shows up automatically
// inside the /obituaries archive AND the memorials browse. The AI card
// pipeline picks it up the same way it picks up imported obits.
//
// The form is intentionally shorter than the full memorial-submit flow
// because families often reach for this in a raw grief moment — every
// field is optional except full_name.
// -----------------------------------------------------------------------------

const CAPTIONS = [
  "Memorial Announcement",
  "Obituary Notice",
  "In Memoriam",
  "Remembering",
  "Celebrating a Life",
  "A Life Remembered",
  "Forever Remembered",
];

export default function ObituariesSubmit() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [captionIdx, setCaptionIdx] = useState(0);
  // Pre-fill any known details from URL query params. Cards on the
  // /obituaries archive link here with the deceased's name/dates/photo/
  // etc. already filled in so a grieving family only has to add
  // personal touches instead of re-typing what we already know.
  const [form, setForm] = useState(() => ({
    full_name: sp.get("name") || "",
    preferred_name: sp.get("known_as") || "",
    age_at_death: sp.get("age") || "",
    death_date: (sp.get("death_date") || "").slice(0, 10),
    location_city: sp.get("city") || "",
    location_state: sp.get("state") || "",
    location_country: sp.get("country") || "",
    portrait_url: sp.get("portrait") || "",
    obit_text: sp.get("text") || "",           // used as short_bio + english_snippet
    source_url: sp.get("source_url") || "",
    author_name: "",
    author_email: "",
    consent: false,
    source_id: sp.get("source_id") || "",
    // Burial ground — free-text label + optional slug when the picker
    // matched a cemetery in our directory. Both fields flow to the
    // memorial doc so the cemetery detail page can auto-populate.
    burial_place: sp.get("burial_place") || "",
    cemetery_slug: sp.get("cemetery_slug") || null,
  }));
  const prefilled = !!(form.full_name || form.portrait_url || form.obit_text);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiLang, setAiLang] = useState("en");

  const draftWithAi = async () => {
    if (!form.full_name.trim()) {
      toast.error("Please add the person's name first — AI needs it to draft.");
      return;
    }
    setAiBusy(true);
    try {
      const facts = {
        full_name: form.full_name.trim(),
        birth_year: form.death_date ? undefined : undefined,
        death_year: form.death_date ? form.death_date.slice(0, 4) : undefined,
        birth_place: undefined,
        death_place: [form.location_city, form.location_state, form.location_country]
          .filter(Boolean).join(", ") || undefined,
        notes: form.obit_text.trim() || undefined,
        language: aiLang,
        tone: "warm",
      };
      const { data } = await http.post("/celebrate/suggest-biography", facts);
      const drafts = Array.isArray(data?.suggestions) ? data.suggestions : [];
      const first = drafts[0] || data?.draft || data?.text || "";
      const joined = drafts.length > 0 ? drafts.join("\n\n") : first;
      if (!joined) throw new Error("Empty AI response");
      set("obit_text", joined);
      toast.success(`AI drafted the obituary in ${AI_LANGS.find((l) => l.code === aiLang)?.name || aiLang}. Please review and edit.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed — please try again in a moment.");
    } finally {
      setAiBusy(false);
    }
  };

  // Little animated caption cycle in the eyebrow so visitors see the
  // page can be called by many warmer names.
  useEffect(() => {
    const t = setInterval(() => setCaptionIdx((i) => (i + 1) % CAPTIONS.length), 3200);
    return () => clearInterval(t);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("Please add the person's full name.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm you have permission from the family to publish this.");
      return;
    }
    setBusy(true);
    const location = (form.location_city || form.location_state || form.location_country)
      ? {
          city: form.location_city || null,
          state: form.location_state || null,
          country: form.location_country || null,
        }
      : null;
    const payload = {
      memorial_type: "death_announcement",
      full_name: form.full_name.trim(),
      preferred_name: form.preferred_name.trim() || null,
      age_at_death: form.age_at_death ? Number(form.age_at_death) : null,
      death_date: form.death_date || null,
      location,
      portrait: form.portrait_url.trim() || null,
      portraits: form.portrait_url.trim() ? [form.portrait_url.trim()] : [],
      short_bio: form.obit_text.trim() || null,
      external_links: form.source_url.trim()
        ? [{ label: "Full obituary", url: form.source_url.trim() }]
        : [],
      submitter: {
        name: form.author_name.trim() || "Anonymous",
        email: form.author_email.trim() || "",
        relationship: null,
      },
      consent: form.consent,
      // If this submission was started from a scraped obituary card,
      // pass the source_id so the backend can link the new family
      // memorial to the original obituary board.
      source_memorial_id: form.source_id || null,
      // Burial ground — persisted both as free text (for the memorial
      // header) and as a `cemetery_slug` (so the cemetery detail page
      // can list this memorial). Either or both may be null.
      burial_place: form.burial_place.trim() || null,
      cemetery_slug: form.cemetery_slug || null,
    };
    try {
      const { data } = await http.post("/memorials", payload);
      const key = data?.slug || data?.id;
      // Best-effort trigger of the auto AI-card pipeline; failure is fine.
      if (key && form.portrait_url.trim()) {
        http.post(`/memorials/${key}/ai-memorial-card/auto`).catch(() => {});
      }
      toast.success("Submitted for review. You'll see it in the archive once approved (usually within a day).");
      if (key) nav(`/celebrate/${key}`);
      else nav("/obituaries");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't publish. Please review the details and try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="obit-submit-page">
      <ObituariesTabs position="top" />

      <Link to="/obituaries" className="inline-flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to obituaries archive
      </Link>

      <div className="overline mb-3" style={{ color: "#8B5CF6" }}>
        <span data-testid="obit-submit-caption">
          {CAPTIONS[captionIdx].toUpperCase()}
        </span>
        <span className="opacity-50"> · DEATH ANNOUNCEMENT</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
        Publish an obituary <br />
        <span style={{ color: "#8B5CF6" }}>with warmth and dignity.</span>
      </h1>
      <p className="mt-3 text-sm md:text-base max-w-2xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Every obituary published here becomes a lasting memorial. Family and
        friends can leave a memory, light a candle, or subscribe to anniversary
        reminders. If a portrait is provided, an AI-created memorial card is
        generated automatically.
      </p>

      {prefilled && (
        <div
          data-testid="obit-prefilled-banner"
          className="mt-6 rounded-xl border p-4 flex items-start gap-3"
          style={{ borderColor: "rgba(139, 92, 246, 0.35)", background: "rgba(139, 92, 246, 0.06)" }}
        >
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#7C3AED" }} />
          <div className="text-sm" style={{ color: "#5B21B6" }}>
            <div className="font-medium mb-0.5">We've filled in what we already know</div>
            <div className="text-xs opacity-80">
              Please double-check the name, dates and photo below — then add any life
              story, achievements, or family details the original obituary was missing.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="obit-submit-form">
        <Section title="The person" hint="Only the full name is required — everything else can be added later by the family.">
          <Field label="Full name" required>
            <input type="text" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                   data-testid="obit-field-full-name" placeholder="Maria Elizabeth Thomas"
                   maxLength={200} className={inputCls} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Preferred / known-as name">
              <input type="text" value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)}
                     data-testid="obit-field-known-as" placeholder="Mary" maxLength={200} className={inputCls} />
            </Field>
            <Field label="Age at passing">
              <input type="number" min="0" max="150" value={form.age_at_death}
                     onChange={(e) => set("age_at_death", e.target.value)}
                     data-testid="obit-field-age" placeholder="82" className={inputCls} />
            </Field>
          </div>
          <Field label="Date of passing">
            <input type="date" value={form.death_date} onChange={(e) => set("death_date", e.target.value)}
                   max={new Date().toISOString().slice(0, 10)}
                   data-testid="obit-field-death-date" className={inputCls} />
          </Field>
        </Section>

        <Section title="Where">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="City / town">
              <input type="text" value={form.location_city} onChange={(e) => set("location_city", e.target.value)}
                     data-testid="obit-field-city" placeholder="Kottayam" maxLength={120} className={inputCls} />
            </Field>
            <Field label="State / region">
              <input type="text" value={form.location_state} onChange={(e) => set("location_state", e.target.value)}
                     data-testid="obit-field-state" placeholder="Kerala" maxLength={120} className={inputCls} />
            </Field>
            <Field label="Country">
              <input type="text" value={form.location_country} onChange={(e) => set("location_country", e.target.value)}
                     data-testid="obit-field-country" placeholder="India" maxLength={120} className={inputCls} />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Burial ground or cremation site (optional)" hint="Start typing to link this memorial to a cemetery in our directory — or just describe it in your own words.">
              <CemeteryPicker
                value={form.cemetery_slug}
                displayValue={form.burial_place}
                onChange={(slug, name) => {
                  setForm((f) => ({ ...f, cemetery_slug: slug, burial_place: name }));
                }}
                testidPrefix="obit-field-cemetery"
              />
            </Field>
          </div>
        </Section>

        <Section title="Photo" hint="A portrait unlocks the AI memorial card. Paste any public image URL for now — image uploads will be added soon.">
          <Field label="Portrait image URL">
            <input type="url" value={form.portrait_url} onChange={(e) => set("portrait_url", e.target.value)}
                   data-testid="obit-field-portrait" placeholder="https://…"
                   maxLength={800} className={inputCls} />
          </Field>
          {form.portrait_url && /^https?:\/\//i.test(form.portrait_url) && (
            <img src={form.portrait_url} alt="preview" onError={(e) => { e.currentTarget.style.display = "none"; }}
                 className="mt-2 w-24 h-24 rounded-xl object-cover border" style={{ borderColor: "var(--wofy-border)" }} />
          )}
        </Section>

        <Section title="The obituary" hint="Two or three sentences is plenty. Family and friends will add more memories later. Or click 'Draft with AI' to turn a few notes into a warm tribute in your language.">
          <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="obit-ai-controls">
            <button
              type="button"
              onClick={draftWithAi}
              disabled={aiBusy || !form.full_name.trim()}
              data-testid="obit-ai-draft-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)",
                color: "#fff",
                opacity: (aiBusy || !form.full_name.trim()) ? 0.55 : 1,
              }}
              title={form.full_name.trim() ? "Draft a warm obituary with AI" : "Add the person's name first"}
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Draft with AI
            </button>
            <label className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest opacity-70">
              in
              <select
                value={aiLang}
                onChange={(e) => setAiLang(e.target.value)}
                data-testid="obit-ai-lang"
                className="rounded-full border px-2 py-1 text-xs bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {AI_LANGS.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </label>
            <span className="text-[11px] opacity-60" style={{ color: "var(--wofy-text-soft)" }}>
              You always keep the final edit.
            </span>
          </div>
          <textarea
            rows={5}
            value={form.obit_text}
            onChange={(e) => set("obit_text", e.target.value)}
            data-testid="obit-field-text"
            placeholder="Maria passed peacefully at home on the morning of…"
            maxLength={2000}
            className={inputCls + " resize-y"}
          />
          <div className="text-[11px] opacity-60 mt-1">{form.obit_text.length}/2000</div>
          <Field label="Link to full obituary (optional)">
            <input type="url" value={form.source_url} onChange={(e) => set("source_url", e.target.value)}
                   data-testid="obit-field-source" placeholder="https://newspaper.com/obituary/…"
                   maxLength={800} className={inputCls} />
          </Field>
        </Section>

        <Section title="You" hint="So the family knows who published this. Not shown publicly.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name">
              <input type="text" value={form.author_name} onChange={(e) => set("author_name", e.target.value)}
                     data-testid="obit-field-author-name" placeholder="Your name" className={inputCls} />
            </Field>
            <Field label="Your email">
              <input type="email" value={form.author_email} onChange={(e) => set("author_email", e.target.value)}
                     data-testid="obit-field-author-email" placeholder="you@example.com" className={inputCls} />
            </Field>
          </div>
        </Section>

        <label className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer" style={{ borderColor: "var(--wofy-border)" }}>
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => set("consent", e.target.checked)}
            data-testid="obit-field-consent"
            className="mt-1"
          />
          <span className="text-sm">
            I confirm I have the family's permission to publish this obituary and
            that the details are accurate to the best of my knowledge.
          </span>
        </label>

        <div className="rounded-xl border p-4 text-xs flex items-start gap-3" style={{ borderColor: "var(--wofy-border)", background: "#FEF3C7" }}>
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
          <span>
            Once published, an AI-created memorial card is generated in the background
            (only when a portrait is supplied). Family members can then leave memories,
            light candles, and subscribe to anniversary reminders.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="text-xs flex items-center gap-1.5" style={{ color: "var(--wofy-text-soft)" }}>
            <Info className="w-3.5 h-3.5" /> You'll be redirected to the published memorial after saving.
          </div>
          <button
            type="submit"
            disabled={busy || !form.full_name.trim() || !form.consent}
            data-testid="obit-submit-btn"
            className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-6 !py-3"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Feather className="w-4 h-4" />}
            Publish obituary
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>

      <ObituariesTabs position="bottom" />
    </div>
  );
}

const inputCls = "w-full rounded-lg border px-3 py-2 text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-offset-1";

function Section({ title, hint, children }) {
  return (
    <div className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "var(--wofy-border)" }}>
      <div>
        <h3 className="font-display text-xl">{title}</h3>
        {hint && <p className="text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}
